import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { FIRST_STATION, LAST_STATION, PLATFORM, TO_DIRECTION } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { IntercityLines } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/intercity-lines.interface';

@Component({
    selector: 'app-lines-between-cities-result-list-item',
    templateUrl: './lines-between-cities-result-list-item.component.html',
    styleUrls: ['./lines-between-cities-result-list-item.component.scss'],
    standalone: false
})
export class LinesBetweenCitiesResultListItemComponent implements OnInit{
  @Input() line: IntercityLines;
  /** Mouse / keyboard activation: open search-line tab with this line (same as former host click). */
  @Output() lineSelected = new EventEmitter<IntercityLines>();
  hasSpecialData: boolean
  toDirectionText: string
  firstStationText: string
  lastStationText: string
  platformText: string
  originCityText: string
  destinationCityText: string
  originStopName: string
  destinationStopName: string
  tripHeadsign: string

  constructor(
    private readonly languageService: LanguageService,
    private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.hasSpecialData = this.line.specialData?.isAccessible || this.line.specialData?.isFlexible || this.line.specialData?.isOrderInAdvanced || this.line.specialMessages.routeMessages?.length > 0 || this.line.specialMessages.stopMessages?.length > 0
    this.initializeMultiLanguageTexts()
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
     this.toDirectionText = TO_DIRECTION[language.language]
     this.firstStationText = FIRST_STATION[language.language]
     this.lastStationText = LAST_STATION[language.language]
     this.platformText = PLATFORM[language.language]
     this.originCityText = this.line.originCity[language.symbol]
     this.destinationCityText = this.line.destinationCity[language.symbol]
     this.originStopName = this.line.originStopName[language.symbol] || this.line.originStopName.he || ''
     this.destinationStopName = this.line.destinationStopName[language.symbol] || this.line.destinationStopName.he || ''
     this.tripHeadsign = this.line.tripHeadsign[language.symbol] || this.line.tripHeadsign.he || ''
    })
  }

  openContactInfoModal() {
    if (this.line.agencyId) {
      this.modalService.openAgencyContactInfoModal(this.line.agencyId, false);
    }
  }

  /** Summary for the focusable card (screen readers). */
  get cardAriaLabel(): string {
    if (!this.line) {
      return '';
    }
    const parts = [
      this.line.routeName,
      this.tripHeadsign ? `${this.toDirectionText ?? ''} ${this.tripHeadsign}`.trim() : '',
      this.firstStationText && this.line.originDepartureTime
        ? `${this.firstStationText} ${this.line.originDepartureTime}`
        : '',
      this.lastStationText && this.line.destinationArrivalTime
        ? `${this.lastStationText} ${this.line.destinationArrivalTime}`
        : '',
    ].filter((p) => !!p && p.trim().length > 0);
    return parts.join(', ');
  }

  /** Real href for SR / open-in-new-tab; matches handleResultClick query params. */
  get lineSearchUrl(): string {
    if (!this.line) {
      return '#';
    }
    return this.router.serializeUrl(
      this.router.createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          tab: eTab.LINES,
          line: this.line.routeName,
          routeId: this.line.routeId,
          oppositeRouteId: this.line.oppositeRouteId,
          boardingStation: this.line.originStopCode,
          dropingStation: this.line.destinationStopCode,
          saveParams: true,
        },
      })
    );
  }

  selectLine(): void {
    this.lineSelected.emit(this.line);
  }

  /**
   * Primary click: keep overlay close + programmatic navigation (existing flow).
   * Modified clicks use the native href (new tab, etc.).
   */
  onLineLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a[href], input, select, textarea')) {
      return;
    }
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    this.selectLine();
  }

  onLineLinkKeyActivate(event: Event): void {
    event.preventDefault();
    this.selectLine();
  }
}
